import { useEffect, useRef, useState } from 'react';
import { LocationPinIcon } from '../assets/icons';

/* ------------------------------------------------------------------ *
 * Google Map location picker. Loads the Maps JS API on demand (no npm
 * dependency) using REACT_APP_GOOGLE_MAPS_API_KEY, drops a draggable pin,
 * and reports the chosen coordinates. Click the map or drag the pin to set
 * the location; the search box jumps to a landmark.
 *
 * Search uses the modern `PlaceAutocompleteElement` (Places API New) — the
 * legacy `Autocomplete` widget is disabled for API projects created after
 * March 2025 — falling back to the legacy widget for older keys.
 * Falls back to a styled notice when no API key is configured.
 * ------------------------------------------------------------------ */

/** Default centre — Alwar, Rajasthan (used until a pin is placed). */
const DEFAULT_CENTER = { lat: 27.553, lng: 76.6346 };

// Load the Maps JS API once, shared across every picker instance.
let mapsPromise: Promise<void> | null = null;
function loadGoogleMaps(key: string): Promise<void> {
  const w = window as unknown as { google?: { maps?: unknown } };
  if (w.google?.maps) return Promise.resolve();
  if (mapsPromise) return mapsPromise;
  mapsPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });
  return mapsPromise;
}

interface GoogleMapPickerProps {
  lat?: number;
  lng?: number;
  onChange: (lat: number, lng: number) => void;
  /** Called after reverse-geocoding a picked point — auto-fills address + pincode. */
  onAddress?: (address: string, pincode: string) => void;
  className?: string;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export default function GoogleMapPicker({ lat, lng, onChange, onAddress, className }: GoogleMapPickerProps) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const mapEl = useRef<HTMLDivElement>(null);
  const searchHost = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const geocoderRef = useRef<any>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onAddressRef = useRef(onAddress);
  onAddressRef.current = onAddress;
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  // Initialise the map once the API is loaded.
  useEffect(() => {
    if (!apiKey) {
      setStatus('error');
      return undefined;
    }
    let cancelled = false;
    loadGoogleMaps(apiKey)
      .then(() => {
        if (cancelled || !mapEl.current) return;
        const g = (window as any).google;
        const start = { lat: lat ?? DEFAULT_CENTER.lat, lng: lng ?? DEFAULT_CENTER.lng };
        const map = new g.maps.Map(mapEl.current, {
          center: start,
          zoom: lat != null && lng != null ? 16 : 13,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        const marker = new g.maps.Marker({ position: start, map, draggable: true });
        mapRef.current = map;
        markerRef.current = marker;
        geocoderRef.current = new g.maps.Geocoder();
        setStatus('ready');

        // Set the pin + report coordinates, then reverse-geocode to auto-fill
        // the street address and pincode.
        const commit = (la: number, lo: number, pan = false) => {
          marker.setPosition({ lat: la, lng: lo });
          if (pan) map.panTo({ lat: la, lng: lo });
          onChangeRef.current(la, lo);
          if (!onAddressRef.current) return;
          geocoderRef.current.geocode(
            { location: { lat: la, lng: lo } },
            (results: any[], gStatus: string) => {
              if (gStatus !== 'OK' || !results?.[0]) {
                // Most common: the Geocoding API isn't enabled on the key.
                console.warn(
                  `[GoogleMapPicker] reverse geocode failed (${gStatus}). ` +
                    'Enable the "Geocoding API" on your Google Maps key to auto-fill address/pincode.',
                );
                return;
              }
              const best = results[0];
              const pin =
                (best.address_components ?? []).find((c: any) => c.types.includes('postal_code'))
                  ?.long_name ?? '';
              onAddressRef.current?.(best.formatted_address ?? '', pin);
            },
          );
        };

        marker.addListener('dragend', (e: any) => commit(e.latLng.lat(), e.latLng.lng()));
        map.addListener('click', (e: any) => commit(e.latLng.lat(), e.latLng.lng()));

        void setupSearch(g, map, searchHost.current, commit);
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  // Keep the pin in sync when coordinates change from outside (e.g. edit prefill).
  useEffect(() => {
    const g = (window as any).google;
    if (status !== 'ready' || lat == null || lng == null || !g || !mapRef.current) return;
    const pos = { lat, lng };
    markerRef.current?.setPosition(pos);
    mapRef.current.panTo(pos);
  }, [lat, lng, status]);

  // No API key → styled fallback with setup guidance.
  if (!apiKey) {
    return (
      <div
        className={[
          'flex h-[260px] flex-col items-center justify-center gap-2 rounded-xl border border-[#e2e8f0] bg-[#f8fafc] text-center',
          className,
        ].join(' ')}
      >
        <LocationPinIcon className="size-7 text-[#94a3b8]" />
        <p className="text-sm font-medium text-[#475569]">Map unavailable</p>
        <p className="max-w-[320px] text-xs text-[#94a3b8]">
          Set <code className="rounded bg-[#eef4ff] px-1 text-brand">REACT_APP_GOOGLE_MAPS_API_KEY</code>{' '}
          to enable the Google Map location picker.
        </p>
      </div>
    );
  }

  return (
    <div className={['relative overflow-hidden rounded-xl border border-[#e2e8f0]', className].join(' ')}>
      {/* Floating search host — the autocomplete widget is mounted here. */}
      <div ref={searchHost} className="absolute left-3 top-3 z-10 w-72 max-w-[75%]" />

      <div ref={mapEl} className="h-[260px] w-full" />

      {status === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#f1f5f9] text-sm text-[#64748b]">
          Loading map…
        </div>
      )}
      {status === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#fef2f2] text-sm text-red-500">
          Could not load Google Maps.
        </div>
      )}
    </div>
  );
}

/**
 * Mount a search box into `host` and wire it to recentre the map + move the pin.
 * Prefers the modern PlaceAutocompleteElement, falling back to the legacy widget.
 */
async function setupSearch(
  g: any,
  map: any,
  host: HTMLElement | null,
  commit: (lat: number, lng: number, pan?: boolean) => void,
) {
  if (!host) return;
  const places = g.maps.places;

  // Modern element (Places API New).
  if (places?.PlaceAutocompleteElement) {
    try {
      const pac = new places.PlaceAutocompleteElement();
      pac.style.width = '100%';
      host.appendChild(pac);
      pac.addEventListener('gmp-select', async (event: any) => {
        const prediction = event?.placePrediction;
        const selected = prediction ? prediction.toPlace() : event?.place;
        if (!selected) return;
        await selected.fetchFields({ fields: ['location', 'viewport'] });
        const loc = selected.location;
        if (!loc) return;
        if (selected.viewport) map.fitBounds(selected.viewport);
        else map.setZoom(16);
        commit(loc.lat(), loc.lng(), true);
      });
      return;
    } catch {
      // fall through to the legacy widget
    }
  }

  // Legacy widget (keys created before March 2025).
  if (places?.Autocomplete) {
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Search coordinates or landmark...';
    input.style.cssText =
      'width:100%;padding:8px 12px;border:1px solid #e2e8f0;border-radius:8px;font-size:12px;box-shadow:0 1px 2px rgba(0,0,0,0.05);outline:none;';
    host.appendChild(input);
    const ac = new places.Autocomplete(input, { fields: ['geometry'] });
    ac.bindTo('bounds', map);
    ac.addListener('place_changed', () => {
      const loc = ac.getPlace()?.geometry?.location;
      if (!loc) return;
      map.setZoom(16);
      commit(loc.lat(), loc.lng(), true);
    });
  }
}
